import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { playIcon, stopIcon } from '@progress/kendo-svg-icons';
import { JsonResponse } from 'TypeGen/json-response';
import { useCallback, useEffect, useState } from 'react';
import { fetchApi } from '../../services/api';

const equipmentFeeDeductionTypeId = 340;
const escrowDeductionTypeId = 232;
const equipmentFeeRecurringAmount = 30;

const escrowOptions = [
  { value: 1500, text: 'Sprinter/Cargo Van - $1500' },
  { value: 2000, text: 'Straight Truck - $2000' },
  { value: 2500, text: 'Tractor - $2500' },
] as any;

type EscrowOption = typeof escrowOptions[number];

export type VehicleDeductionStatus = {
  EquipmentFeeActive: boolean;
  EquipmentFeeAmount?: number | null;
  EscrowActive: boolean;
  EscrowAmount?: number | null;
};

type Props = {
  vehicleId: number;
  assetHardwareId: number | null;
  removedDateTime: Date | null;
};

const defaultStatus: VehicleDeductionStatus = {
  EquipmentFeeActive: false,
  EquipmentFeeAmount: null,
  EscrowActive: false,
  EscrowAmount: null,
};

const VehicleHardwareDeductionControls = ({ vehicleId, assetHardwareId, removedDateTime }: Props) => {
  const [deductionStatus, setDeductionStatus] = useState<VehicleDeductionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [escrowDialogOpen, setEscrowDialogOpen] = useState<boolean>(false);
  const [selectedEscrowOption, setSelectedEscrowOption] = useState<EscrowOption>(escrowOptions[0]);
  const [releaseLoading, setReleaseLoading] = useState<boolean>(false);

  const fetchDeductionStatus = useCallback(async () => {
    setLoading(true);
    await fetchApi(`/api/Asset/VehicleHardwareDeductionStatus/${vehicleId}`)
      .then((data: VehicleDeductionStatus) => {
        setDeductionStatus(data)
      }).catch(() => setDeductionStatus(defaultStatus))
      .finally(() => setLoading(false));
  }, [vehicleId]);

  useEffect(() => {
    fetchDeductionStatus();
  }, [fetchDeductionStatus]);

  const formatCurrency = (value?: number | null): string => {
    if (value === undefined || value === null)
      return '';
    return `$${value.toFixed(2)}`;
  };

  const handleDeductionToggle = async (deductionTypeId: number, start: boolean, amountOverride?: number) => {
    if (actionInProgress)
      return;

    const isEscrowFee = deductionTypeId === escrowDeductionTypeId;
    const amount = start
      ? isEscrowFee
        ? amountOverride ?? 0
        : equipmentFeeRecurringAmount
      : 0;

    if (start) {
      if (isEscrowFee) {
        if (!amount || amount <= 0) {
          alert('Please select an escrow amount.');
          return;
        }

        const confirmation = `Are you sure you want to start the escrow fee deduction for ${formatCurrency(amount)} amortized over 12 pay periods?`;
        if (!confirm(confirmation))
          return;
      }
      else {
        const confirmation = `Are you sure you want to start the equipment deduction for ${formatCurrency(amount)} each pay period?`;
        if (!confirm(confirmation))
          return;
      }
    }
    else {
      const confirmation = isEscrowFee
        ? 'Are you sure you want to stop the escrow deduction?'
        : 'Are you sure you want to stop the equipment fee deduction?';

      if (!confirm(confirmation))
        return;
    }

    setActionInProgress(true);

    await fetchApi(`/api/Asset/VehicleHardwareDeductionToggle/${vehicleId}`, {
      deductionTypeId,
      start,
      amount,
    }, 'POST')
      .then(async (response: JsonResponse) => {
        if (response.Success) {
          const successMessage = start
            ? isEscrowFee ? 'Escrow deduction started successfully.' : 'Equipment fee deduction started successfully.'
            : isEscrowFee ? 'Escrow deduction stopped successfully.' : 'Equipment fee deduction stopped successfully.';
          alert(successMessage);
          await fetchDeductionStatus();
        }
        else {
          alert(response.ErrorMessage);
        }
      })
      .catch((err) => alert(err))
      .finally(() => setActionInProgress(false));
  };

  const equipmentFeeActive = deductionStatus?.EquipmentFeeActive ?? false;
  const escrowActive = deductionStatus?.EscrowActive ?? false;
  const equipmentFeeAmount = deductionStatus?.EquipmentFeeAmount ?? null;
  const escrowAmount = deductionStatus?.EscrowAmount ?? null;
  const deductionBusy = loading || actionInProgress;
  const canReleaseEscrow = assetHardwareId !== null && removedDateTime !== null;

  const releaseEscrow = () => {
    if (!canReleaseEscrow)
      return;

    setReleaseLoading(true);

    const data = {
      AssetHardwareID: assetHardwareId,
      RemovedDateTime: removedDateTime,
    };

    fetchApi('/api/Asset/SendSamsaraEscrowReleaseEmail', data, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          alert('Escrow release notification sent.');
        }
        else {
          alert(response.ErrorMessage ?? 'Unable to send escrow release notification');
        }
      })
      .catch((e) => {
        if (!e?.status) alert('Unable to send escrow release notification');
      })
      .finally(() => setReleaseLoading(false));
  };

  const openEscrowDialog = () => {
    const defaultOption = escrowAmount ? escrowOptions.find((option: { value: number; }) => option.value === Number(escrowAmount)) : undefined;
    setSelectedEscrowOption(defaultOption ?? escrowOptions[0]);
    setEscrowDialogOpen(true);
  };

  const closeEscrowDialog = () => setEscrowDialogOpen(false);

  const confirmEscrowStart = async () => {
    closeEscrowDialog();
    await handleDeductionToggle(escrowDeductionTypeId, true, selectedEscrowOption.value);
  };

  return (
    <div className="row pt-3">
      <div className="col-md-12">
        <div className="border rounded p-3 bg-light-subtle">
          <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
            <strong>Hardware Deductions</strong>
            <div className="d-flex align-items-center gap-2">
              {loading && <span className="text-muted small">Loading status...</span>}
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center py-2">
            <div>
              <div className="fw-bold">Equipment Fee</div>
              <div className="text-muted small">
                {equipmentFeeActive ? `Active${equipmentFeeAmount ? ` (${formatCurrency(equipmentFeeAmount)} total)` : ''}` : 'Inactive'}
              </div>
            </div>
            <Button
              themeColor={equipmentFeeActive ? 'error' : 'primary'}
              disabled={deductionBusy}
              svgIcon={equipmentFeeActive ? stopIcon : playIcon}
              onClick={() => handleDeductionToggle(equipmentFeeDeductionTypeId, !equipmentFeeActive)}
            >
              {equipmentFeeActive ? 'Stop' : 'Start'}
            </Button>
          </div>
          <div className="d-flex justify-content-between align-items-center py-2 border-top">
            <div>
              <div className="fw-bold">Escrow</div>
              <div className="text-muted small">
                {escrowActive ? `Active${escrowAmount ? ` (${formatCurrency(escrowAmount)} per pay)` : ''}` : 'Inactive'}
              </div>
            </div>
            <div>
              {escrowActive ? (
                <Button
                  disabled
                  themeColor="primary"
                  svgIcon={playIcon}
                >
                  Active
                </Button>
              ) : (
                <Button
                  themeColor="primary"
                  disabled={deductionBusy}
                  onClick={openEscrowDialog}
                  svgIcon={playIcon}
                >
                  Start
                </Button>
              )}

              {canReleaseEscrow && (
                <Button
                  className='ml-1'
                  themeColor='warning'
                  onClick={releaseEscrow}
                  disabled={releaseLoading}
                >
                  {releaseLoading ? 'Sending...' : 'Release Escrow'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {escrowDialogOpen && (
        <Dialog title="Start Escrow Deduction" onClose={closeEscrowDialog}>
          <p className="mb-3">Select the escrow amount to amortize over 12 pay periods.</p>
          <DropDownList
            data={escrowOptions}
            dataItemKey="value"
            textField="text"
            value={selectedEscrowOption}
            onChange={(event) => setSelectedEscrowOption(event.value as EscrowOption)}
            className="w-100 mb-3"
          />
          <DialogActionsBar>
            <Button
              onClick={closeEscrowDialog}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmEscrowStart}
              themeColor="primary"
              disabled={actionInProgress}
            >
              Start Deduction
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default VehicleHardwareDeductionControls;